import { z } from 'zod';
/**
 * POST /vehicles/ingest wire contract (camelCase). Today's only producer is the
 * external Grok acquisition bot (its own infrastructure); the `Hermes*` names are
 * historical — the contract was first written for hermes Job E, and the
 * in-repo services/hermes was removed on 2026-10-04. THIS file is now the
 * source of truth.
 *
 * Origin (both files no longer exist): cars-finder-api/app/models/hermes_lot.py
 * (pydantic, extra="ignore" — mirrored here by zod's default strip behaviour),
 * itself the mirror of hermes/state/parse_lot.py:build().
 *
 * The camelCase↔snake_case mapping tables below are the canonical copy of the
 * tables duplicated in app/api/vehicles_ingest.py and
 * api-node src/modules/ingest/ingest.mapper.ts.
 */
export const HermesVehicleSchema = z.object({
    make: z.string().nullish(),
    model: z.string().nullish(),
    brand: z.string().nullish(),
    year: z.number().int().nullish(),
    type: z.string().nullish(),
    vin: z.string().nullish(),
    mileageKm: z.number().int().nullish(),
    registrationDate: z.string().nullish(),
    fuelType: z.string().nullish(),
    transmission: z.string().nullish(),
    enginePowerKw: z.number().int().nullish(),
    engineCcm: z.number().int().nullish(),
    /**
     * true when engineCcm was derived from a litre figure (2.0 → 2000) rather than
     * read as cm³ — close, but not exact enough for customs (2.0 L may be 1968 cm³).
     */
    engineCcmApprox: z.boolean().nullish(),
    /** One of ENGINE_TYPES; any other string is normalised to 'other'. Ukrainian label rides in ua.engineType. */
    engineType: z.string().nullish(),
    gears: z.number().int().nullish(),
    drive: z.string().nullish(),
    color: z.string().nullish(),
    interiorColor: z.string().nullish(),
    doors: z.number().int().nullish(),
    seats: z.number().int().nullish(),
    condition: z.string().nullish(),
    accident: z.string().nullish(),
    status: z.string().nullish(),
});
/** What kind of lot this is — a car auction also sells motorbikes, bicycles and trailers. */
export const LOT_TYPES = [
    'car',
    'motorcycle',
    'bicycle',
    'ebike',
    'trailer',
    'caravan',
    'truck',
    'van',
    'other',
];
export const LotTypeSchema = z.enum(LOT_TYPES);
/**
 * Propulsion as customs sees it. `mhev` (48 V mild hybrid: the electric motor
 * only assists) is kept apart from full `hybrid` on purpose — whether customs
 * treats it as a hybrid or as a plain petrol/diesel car is a rule-set decision
 * for the landed-cost engine, and the source must not pre-empt it.
 */
export const ENGINE_TYPES = [
    'petrol',
    'diesel',
    'electric',
    'hybrid',
    'mhev',
    'phev',
    'lpg',
    'cng',
    'hydrogen',
    'other',
];
export const EngineTypeSchema = z.enum(ENGINE_TYPES);
/**
 * Lenient wire string → closed enum. The wire fields are plain strings on
 * purpose: a value the bot adds later lands as 'other' instead of 400-ing the
 * whole lot (lots are scraped once — a rejected lot is lost). Absent/blank →
 * undefined, so "unknown" is never stored as a guess.
 */
function normalizeEnum(values, value) {
    const key = value?.trim().toLowerCase();
    if (!key)
        return undefined;
    return values.includes(key) ? key : 'other';
}
/** Wire `lotType` → LotType (see normalizeEnum). */
export function normalizeLotType(value) {
    return normalizeEnum(LOT_TYPES, value);
}
/** Wire `vehicle.engineType` → EngineType (see normalizeEnum). */
export function normalizeEngineType(value) {
    return normalizeEnum(ENGINE_TYPES, value);
}
/** Integer CHF after hermes clean_price. */
export const HermesPricingSchema = z.object({
    currentBid: z.number().int().nullish(),
    currentValue: z.number().int().nullish(),
    catalogPrice: z.number().int().nullish(),
    repairCosts: z.number().int().nullish(),
    equipmentPrice: z.number().int().nullish(),
    accessoriesPrice: z.number().int().nullish(),
});
export const HermesDamageSchema = z.object({
    damageDescription: z.string().nullish(),
    damageLocation: z.string().nullish(),
    preDamage: z.string().nullish(),
    usableParts: z.string().nullish(),
    defects: z.string().nullish(),
});
/** hermes:ai marker — 9 qwen fields, money in USD. */
export const HermesAISchema = z.object({
    damageSeverity: z.string().nullish(),
    repairCost: z.number().nullish(),
    priceSuggest: z.number().nullish(),
    marketValue: z.number().nullish(),
    profitMargin: z.number().nullish(),
    investmentRecommendation: z.string().nullish(),
    summaryUa: z.string().nullish(),
    prosUa: z.array(z.string()).nullish(),
    consUa: z.array(z.string()).nullish(),
});
export const HermesLotPayloadSchema = z.object({
    /** Alpicar lot UUID (= listing_id). */
    uuid: z.string(),
    url: z.string().nullish(),
    lang: z.string().nullish(),
    /** ISO datetime of the scrape. */
    scrapedAt: z.string().nullish(),
    title: z.string().nullish(),
    insurer: z.string().nullish(),
    source: z.string().nullish(),
    auctionName: z.string().nullish(),
    auctionTag: z.string().nullish(),
    auctionCountdown: z.string().nullish(),
    /** ISO datetime or absent. */
    auctionEndDate: z.string().nullish(),
    /** One of LOT_TYPES; any other string is normalised to 'other' (see normalizeLotType). */
    lotType: z.string().nullish(),
    vehicle: HermesVehicleSchema.default({}),
    pricing: HermesPricingSchema.default({}),
    damage: HermesDamageSchema.default({}),
    importantInformation: z.string().nullish(),
    equipment: z.array(z.string()).nullish(),
    /** CDN URLs (S3 upload happens after ingest). */
    images: z.array(z.string()).nullish(),
    /** hermes:ua marker — translations for the TRANSLATABLE keys. */
    ua: z.record(z.union([z.string(), z.array(z.string())])).nullish(),
    ai: HermesAISchema.nullish(),
    /** AUTO.RIA comparables: min / median / max + stats. Stored verbatim as market_ua. */
    marketUa: z.record(z.unknown()).nullish(),
    /**
     * Lot digest for the customer (title / confidence / basis / bullets / text /
     * method / factors). Stored verbatim as summary_ua. Not to be confused with
     * `ai.summaryUa`, a plain string that lands in ai_car_analysis.
     */
    summaryUa: z.record(z.unknown()).nullish(),
    /**
     * Source fields with no dedicated slot (homologation no., eco code, last
     * inspection, location, keys, service book, COC, weight, …). Stored verbatim
     * as `extra`; the bot deliberately leaves out highest_bidder (privacy).
     */
    extra: z.record(z.unknown()).nullish(),
    /** Linear-issue id, tracing only — never stored. */
    linearIssueId: z.string().nullish(),
});
export const IngestResponseSchema = z.object({
    success: z.boolean().default(true),
    listing_id: z.string(),
    /** true — new document; false — update. */
    created: z.boolean(),
    ai_saved: z.boolean().default(false),
    s3_enqueued: z.boolean().default(false),
});
// ── camelCase ↔ snake_case mapping tables (canonical copy) ────────────────
/** vehicle.<camel> → alpicar_cars.<snake>, wrapped multilingual {en, ua}. */
export const MULTILINGUAL_VEHICLE_FIELD_MAP = {
    type: 'type',
    fuelType: 'fuel_type',
    transmission: 'transmission',
    drive: 'drive',
    color: 'color',
    interiorColor: 'interior_color',
    condition: 'condition',
    accident: 'accident',
    status: 'status',
};
/** damage.<camel> → alpicar_cars.<snake>, wrapped multilingual {en, ua}. */
export const DAMAGE_FIELD_MAP = {
    damageDescription: 'damage_description',
    damageLocation: 'damage_location',
    preDamage: 'pre_damage',
    usableParts: 'usable_parts',
    defects: 'defects',
};
/** pricing.<camel> → alpicar_cars.<snake>, numeric passthrough (currentBid is special: stringified). */
export const PRICING_FIELD_MAP = [
    ['currentValue', 'current_value'],
    ['catalogPrice', 'catalog_price'],
    ['repairCosts', 'repair_costs'],
    ['equipmentPrice', 'equipment_price'],
    ['accessoriesPrice', 'accessories_price'],
];
//# sourceMappingURL=ingest.js.map